import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DashboardLoanTransactionDetailModelDto } from '@treasury/api/channel';
import { LoanAccountDetail } from '@treasury/domain/accounts/types/loan-account-details.entity';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { Holiday, HolidaysService } from '@treasury/domain/dates';
import { Loan, LoanService } from '@treasury/domain/loans';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-button-group';
import '@treasury/presentation/components/tm-currency-field';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-toggle-content';
import { formatUsdCurrency } from '@treasury/utils';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import {
    DatePickerDate,
    inactiveHolidays,
    inactiveWeekends,
} from '../../../utilities/disabled-date.functions';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../../components/loan-details';

const paymentOptions = [
    {
        label: 'Regular',
        value: 'Regular Payment',
    },
    {
        label: 'Principal Only',
        value: 'Principal Only',
    },
];

export const tagName = 'loan-payment-editable';
@customElement(tagName)
export class LoanPaymentEditable extends TmBaseComponent {
    @property({ type: Object })
    public loan = new Loan();

    @InjectProperty({ async: true, token: FeatureFlagService })
    declare private ffServicePromise: Promise<FeatureFlagService>;

    @InjectProperty()
    declare private loanService: LoanService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private holidaysService: HolidaysService;

    @state()
    private hasAdditionalLoanDetailsFeature = false;

    @state()
    private loanPaymentFromAccounts: CompanyAccount[] = [];

    @state()
    private filteredLoanPaymentFromAccounts: CompanyAccount[] = [];

    @state()
    private loanPaymentFromAccountOptions: { value: CompanyAccount; label: string }[] = [];

    @state()
    private selectedFromAccount: CompanyAccount | undefined;

    @state()
    private loanPaymentToAccounts: CompanyAccount[] = [];

    @state()
    private filteredLoanPaymentToAccounts: CompanyAccount[] = [];

    @state()
    private loanPaymentToAccountOptions: { value: CompanyAccount; label: string }[] = [];

    @state()
    private selectedToAccount: CompanyAccount | undefined;

    @state()
    private amount: string | undefined;

    @state()
    private paymentDate: string | undefined;

    @state()
    private holidays: Array<Holiday> = [];

    @state()
    private loading = false;

    @state()
    private showOptionalFields = false;

    protected async firstUpdated() {
        this.loading = true;
        const ffService = await this.ffServicePromise;
        this.hasAdditionalLoanDetailsFeature = await ffService.isEnabled(
            Feature.AdditionalLoanDetails
        );
        this.loan.paymentOptionType = paymentOptions[0].value;
        if (this.loan.amount) {
            this.amount = this.loan.amount.toString();
        }
        await this.getFromAccounts();
        await this.getToAccounts();
        this.loading = false;
    }

    protected render() {
        return html`<tm-section
            ><tm-form>
                <tm-form-row required noWrap .label=${'From'}>
                    <tm-slider
                        header="Select From Account"
                        .value=${this.loan.fromAccount?.accountDisplayLabel
                            ? this.loan.fromAccount?.accountDisplayLabel
                            : undefined}
                        .placeholder=${'Select account'}
                        >${this.renderFromAccountsList()}</tm-slider
                    >
                    ${this.loan.fromAccount.availableBalance
                        ? html`<span
                              class="text-xs text-secondary mb-1 w-full text-right"
                              slot="extra"
                              >Available:
                              ${formatUsdCurrency(
                                  this.loan.fromAccount.availableBalance || 0
                              )}</span
                          >`
                        : nothing}
                </tm-form-row>
                <tm-form-row required noWrap .label=${'To loan'}>
                    <tm-slider
                        header="Select Loan Account"
                        .value=${this.loan.toAccount?.accountDisplayLabel
                            ? this.loan.toAccount?.accountDisplayLabel
                            : undefined}
                        .placeholder=${'Select account'}
                        >${this.renderToAccountsList()}</tm-slider
                    >
                    ${this.renderLoanAccountSummary()}
                </tm-form-row>
                <tm-form-row required noWrap .label=${'Type'}>
                    <tm-segmented-control
                        class="flex justify-end py-2"
                        .options=${paymentOptions}
                        @value-changed=${({ detail }: TmValueChanged<SelectItemVm<string>>) => {
                            if (!detail.value) {
                                return;
                            }
                            this.loan.paymentOptionType = detail.value.value;
                            this.updateLoan();
                        }}
                    ></tm-segmented-control>
                </tm-form-row>
                <tm-form-row required noWrap .label=${'Amount'}>
                    <tm-currency-field
                        class="max-w-[130px] my-2"
                        .initial=${this.amount ? this.amount : this.loan.amount.toString()}
                        .required=${true}
                        @value-changed=${(e: TmValueChanged<string>) => {
                            const { value } = e.detail;
                            if (!value) {
                                return;
                            }

                            this.amount = value;
                            this.loan.amount = parseFloat(this.amount);
                            this.updateLoan();
                        }}
                    ></tm-currency-field>
                </tm-form-row>
                <tm-form-row required noWrap .label=${'Payment date'}>
                    <tm-date-picker
                        class="my-2 max-w-[150px]"
                        .required=${true}
                        .isDateDisabled=${(date: DatePickerDate) =>
                            inactiveWeekends(date) || inactiveHolidays(date, this.holidays)}
                        .initial=${new Date()}
                        .min=${new Date()}
                        @value-changed=${(e: TmValueChanged<Date>) => {
                            const { value, isValid } = e.detail;
                            if (!isValid || !value) {
                                return;
                            }
                            this.paymentDate = value.toDateString();
                            this.loan.transferDate = value.toDateString();
                            this.updateLoan();
                        }}
                    ></tm-date-picker>
                </tm-form-row>
                <tm-toggle-content>
                    <tm-form-row noWrap .label=${'Memo'}>
                        <tm-text-field
                            .maxLength=${30}
                            .initial=${this.loan.memo}
                            @value-changed=${(e: CustomEvent) => {
                                this.loan.memo = e.detail.value;
                                this.updateLoan();
                            }}
                        ></tm-text-field>
                    </tm-form-row>
                </tm-toggle-content>
            </tm-form>
        </tm-section>`;
    }

    private async getFromAccounts() {
        this.loanPaymentFromAccounts = await this.loanService.getLoanPaymentFromAccounts();
        this.loanPaymentFromAccountOptions = this.loanPaymentFromAccounts.map(account => ({
            value: account,
            label: account.name,
        }));
        this.holidays = await this.holidaysService.getHolidays();
        this.filteredLoanPaymentFromAccounts = this.loanPaymentFromAccounts;
    }

    private async getToAccounts() {
        this.loanPaymentToAccounts = await this.loanService.getLoanPaymentToAccounts(
            this.selectedFromAccount?.id?.toString() || ''
        );
        this.loanPaymentToAccountOptions = this.loanPaymentToAccounts.map(account => ({
            value: account,
            label: account.name,
        }));
        this.filteredLoanPaymentToAccounts = this.loanPaymentToAccounts;
    }

    private updateLoan() {
        this.dispatchEvent(new CustomEvent('update', { detail: this.loan }));
    }

    private renderFromAccountsList() {
        const renderAccount = (item: CompanyAccount) =>
            html` <div class="flex flex-row justify-between">
                <div class="flex flex-col">
                    <div class="text-sm">${item.name}</div>
                    <div class="text-xs text-secondary">${item.accountDisplayLabel}</div>
                </div>
                <div class="flex flex-col text-right">
                    <div class="text-sm">${formatUsdCurrency(item.availableBalance || 0)}</div>
                    <div class="text-xs text-secondary">Available</div>
                </div>
            </div>`;
        return html`<tm-section
            ><tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filteredLoanPaymentFromAccounts = filterTableResults(
                        e,
                        this.loanPaymentFromAccounts,
                        ['name', 'accountDisplayLabel', 'number', 'type', 'availableBalance']
                    );
                }}
            ></tm-list-header>
            <vaadin-grid
                .items=${this.filteredLoanPaymentFromAccounts}
                @active-item-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.selectedFromAccount = e.detail.value;
                    this.loan.fromAccount = e.detail.value;
                    this.getToAccounts();
                    e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                    this.updateLoan();
                }}
            >
                <vaadin-grid-column
                    ${columnBodyRenderer(
                        renderAccount as GridColumnBodyLitRenderer<CompanyAccount>
                    )}
                ></vaadin-grid-column></vaadin-grid
        ></tm-section>`;
    }

    private renderToAccountsList() {
        const renderNameAndNumber = (item: CompanyAccount) =>
            html` <div class="flex flex-col">
                <div class="text-sm">${item.name}</div>
                <div class="text-xs text-secondary">${item.accountDisplayLabel}</div>
            </div>`;
        const renderAmountAndType = (item: CompanyAccount) =>
            html`<div class="flex flex-col">
                <div class="text-sm">${formatUsdCurrency(item.currentBalance || 0)}</div>
                <div class="text-xs text-secondary">Current</div>
            </div>`;
        return html`<tm-section
            ><tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filteredLoanPaymentToAccounts = filterTableResults(
                        e,
                        this.loanPaymentToAccounts,
                        ['name', 'accountDisplayLabel', 'number', 'type', 'availableBalance']
                    );
                }}
            ></tm-list-header
            ><vaadin-grid
                .items=${this.filteredLoanPaymentToAccounts}
                @active-item-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.selectedToAccount = e.detail.value;
                    this.loan.toAccount = e.detail.value;
                    this.amount = this.selectedToAccount?.loanPaymentAmountDue?.toString();
                    e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                    this.updateLoan();
                }}
                ><vaadin-grid-column
                    ${columnBodyRenderer(
                        renderNameAndNumber as GridColumnBodyLitRenderer<CompanyAccount>
                    )}
                ></vaadin-grid-column
                ><vaadin-grid-column
                    text-align="end"
                    ${columnBodyRenderer(
                        renderAmountAndType as GridColumnBodyLitRenderer<CompanyAccount>
                    )}
                ></vaadin-grid-column></vaadin-grid
        ></tm-section>`;
    }

    private renderLoanAccountSummaryLegacy() {
        return html` <div class="text-xs text-left" slot="extra">
            <jhd-data-field
                label="Outstanding balance"
                .value=${formatUsdCurrency(this.loan.toAccount.currentBalance || 0)}
            ></jhd-data-field>
            <jhd-data-field
                label="Payment amount due"
                .value=${formatUsdCurrency(this.loan.toAccount.loanPaymentAmountDue || 0)}
            ></jhd-data-field>
            <jhd-data-field
                label="Payment due date"
                .value=${this.loan.toAccount.loanPaymentAmountDue
                    ? format(new Date(this.loan.toAccount.loanPaymentDueDate || ''), 'P')
                    : ''}
            ></jhd-data-field>
        </div>`;
    }

    private renderLoanAccountSummary() {
        if (!this.loan.toAccount.id) {
            return nothing;
        }

        if (!this.hasAdditionalLoanDetailsFeature) {
            return this.renderLoanAccountSummaryLegacy();
        }

        const account =
            this.loan.toAccount.toDto() as unknown as DashboardLoanTransactionDetailModelDto;
        return html` <div class="border-t border-dashed" slot="extra">
            <tm-toggle-content class="-ml-4" label="loan details">
                <loan-details
                    class="ml-4 mt-4"
                    .loan=${new LoanAccountDetail(account)}
                ></loan-details>
            </tm-toggle-content>
        </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                    --jhd-field-label-width: 150px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanPaymentEditable;
    }
}
